import {
  MdsItemKind,
  MdsItemListAsyncSessionRowData,
  MdsItemListRowData,
} from "@/design-system/components/item-list/types";
import { MdsItemListRowType, MdsItemListSize } from "@/design-system/components/item-list/types";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  reaction,
  runInAction,
} from "mobx";
import { SessionObservable } from "@/store/sessions/SessionObservable";
import { Maybe } from "@/domains/common/types";
import { SortByKind } from "@/modules/lenses/types";
import { liveQuery, Subscription } from "dexie";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { EventContext } from "@/domains/metrics/context";
import { NotesListFilter } from "@/modules/filters/notes/NotesListFilter";
import { SessionRecord } from "@/store/sessions/types";
import { MDSRowItemSize } from "@/mds";

export class SessionsListPageStore extends AppSubStore {
  supportsAddToCollectionListAction = true;
  supportsMoveToTrashListAction = true;
  listState: ListStateObservable;
  sessionsFilterQuery: string = "";

  isLoading: boolean;
  subscriptionInitialized: boolean;
  liveQuerySubscription: Maybe<Subscription> = undefined;
  subscribedSessionRows: SessionRecord[] = [];

  eventContext = EventContext.TemplatesMultiselectActions;

  filters: NotesListFilter;

  private readonly supportedSortOptions: SortByKind[] = [
    SortByKind.LastModified,
    SortByKind.LastCreated,
    SortByKind.LastViewed,
    SortByKind.LastShared,
    SortByKind.LastUsed,
    SortByKind.Alphabetical,
  ];

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.listState = new ListStateObservable({ ...injectedDeps, listStateProvider: this });

    // TODO: This needs to become a SessionsListFilter
    this.filters = new NotesListFilter(this.store, this.supportedSortOptions);

    this.isLoading = true;
    this.subscriptionInitialized = false;

    makeObservable<this, "generateItemDropdown" | "initializeLiveQuery" | "supportedSortOptions">(
      this,
      {
        isEmpty: true,
        mapSessionToRow: true,
        resetState: true,
        supportedSortOptions: true,
        initializeLiveQuery: action,
        liveQuerySubscription: observable,
        subscriptionInitialized: observable,

        subscribedSessionRows: observable,
        itemRows: computed,

        supportsAddToCollectionListAction: observable,
        supportsMoveToTrashListAction: observable,
        sessionsFilterQuery: observable,
        listState: false,
        eventContext: false,

        isLoading: observable,
        generateItemDropdown: false,
        orderedItemIds: computed,

        initialize: action,
        filters: false,
      }
    );

    reaction(
      () => this.subscriptionInitialized,
      initialized => {
        if (initialized) setTimeout(() => runInAction(() => (this.isLoading = false)), 100);
      }
    );

    onBecomeObserved(this, "subscribedSessionRows", () => this.initializeLiveQuery());
  }

  get isEmpty() {
    return this.subscribedSessionRows.length === 0;
  }

  get itemRows() {
    const output: MdsItemListRowData[] = [];

    for (const item of this.subscribedSessionRows) {
      output.push(this.mapSessionToRow(item.id));
    }

    if (output.length) {
      output.push({
        type: MdsItemListRowType.Padding,
        key: "padding",
        payload: { height: 50 },
      });
    }

    return output;
  }

  mapSessionToRow(sessionId: string): MdsItemListAsyncSessionRowData {
    return {
      type: MdsItemListRowType.AsyncSession,
      key: sessionId,
      size: MdsItemListSize.XLarge,
      payload: {
        sessionId,
        itemRow: (session: SessionObservable) => {
          return {
            type: MdsItemListRowType.Item,
            key: session.id,
            size: MDSRowItemSize.XLarge,
            payload: {
              id: session.id,
              kind: MdsItemKind.Session,
              label: session.id,
              onClick: () => this.store.navigation.goToSession({ sessionId }),
              listState: this.listState,
            },
          };
        },
      },
    };
  }

  get orderedItemIds() {
    return this.itemRows.map(row => row.key);
  }

  resetState = () => {};

  initialize = async () => {
    this.initializeLiveQuery();
  };

  initializeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();

    this.liveQuerySubscription = liveQuery(() => {
      return this.store.sessions.localTable.toCollection().keys();
    }).subscribe({
      next: items => {
        runInAction(() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.subscribedSessionRows = items.map((item: any) => ({ id: item }));
          this.subscriptionInitialized = true;
        });
      },
    });
  }
}
